import { difference, pipe, union } from 'ramda';
import { Category } from 'api/timesheets/categories';
import { Loaded } from 'core/state';
import { CategoriesStore, from, to } from './core';

export type CategoryCreationRequest = Omit<Category, 'id' | 'deleted'>;

export const creating = (request: CategoryCreationRequest, id: string) =>
  pipe(
    from,
    ({
      byId,
      ids,
      creating: { byId: creatingById, ids: creatingIds },
    }: CategoriesStore): CategoriesStore => {
      const [, categoryIds = []] = ids;
      const category = {
        ...request,
        id: -1 - creatingIds.length,
      } as unknown as Category;

      return {
        byId: { ...byId, [category.id]: Loaded(category) },
        ids: Loaded(union(categoryIds, [category.id])),
        creating: {
          byId: { ...creatingById, [id]: category.id },
          ids: union(creatingIds, [id]),
        },
      };
    },
    to,
  );

export const created = (category: Category, id: string) =>
  pipe(
    from,
    (store: CategoriesStore): CategoriesStore => {
      if (!store.creating.ids.includes(id)) {
        return store;
      }
      const {
        byId,
        ids,
        creating: { byId: creatingById, ids: creatingIds },
      } = store;
      const [, taskIds = []] = ids;
      const idToRemove = creatingById[id];
      const { [idToRemove]: _creatingId, ...otherIds } = byId;
      const { [id]: _creatingReference, ...otherCreatingReferences } =
        creatingById;

      return {
        byId: { ...otherIds, [category.id]: Loaded(category) },
        ids: Loaded(difference(union(taskIds, [category.id]), [idToRemove])),
        creating: {
          byId: otherCreatingReferences,
          ids: difference(creatingIds, [id]),
        },
      };
    },
    to,
  );

export const uncreated = (id: string) =>
  pipe(
    from,
    (store: CategoriesStore): CategoriesStore => {
      if (!store.creating.ids.includes(id)) {
        return store;
      }
      const {
        byId,
        ids,
        creating: { byId: creatingById, ids: creatingIds },
      } = store;
      const [, taskIds = []] = ids;
      const idToRemove = creatingById[id];
      const { [idToRemove]: _creatingId, ...otherIds } = byId;
      const { [id]: _creatingReference, ...otherCreatingReferences } =
        creatingById;

      return {
        byId: otherIds,
        ids: Loaded(difference(taskIds, [idToRemove])),
        creating: {
          byId: otherCreatingReferences,
          ids: difference(creatingIds, [id]),
        },
      };
    },
    to,
  );
